import { useState } from 'react';
import { toast } from 'sonner';
import { useUpdateUser } from '../../stores/users';
import { useLiveForm } from '../../hooks/useLiveForm';
import { usePresenceStore, useRegisterPresence } from '../../stores/presence';
import { useConnectionStore } from '../../stores/connection';
import { PresenceAvatars } from '../ui/presence-avatars';
import { LiveField } from '../assets/LiveField';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { User } from '../../types/api';

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function EditUserDialog({ open, onOpenChange, user }: EditUserDialogProps) {
  const [error, setError] = useState('');
  const updateMutation = useUpdateUser();

  const liveForm = useLiveForm({
    entity: "user",
    entityId: user?.user_id,
    topic: `users.item.${user?.user_id}`,
    enabled: open && user !== null,
  });

  useRegisterPresence("user", user?.user_id, open && user !== null);

  usePresenceStore.live({ entity: "user" });
  const presenceMap = usePresenceStore(s => s.presence);
  const instanceId = useConnectionStore(s => s.instanceId);
  const viewers = (user?.user_id && presenceMap?.[user.user_id]) || [];

  const nameField = liveForm.field("name");
  const roleField = liveForm.field("role");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!user) return;

    try {
      if (liveForm.isDirty) {
        const result = await liveForm.submit() as { ok: boolean; applied: number; edits: Record<string, unknown> } | undefined;
        if (result?.edits && Object.keys(result.edits).length > 0) {
          await updateMutation.mutate({
            userId: user.user_id,
            ...result.edits,
          });
        }
      }
      toast.success('User updated successfully');
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  }

  function handleClose() {
    liveForm.discard();
    setError('');
    onOpenChange(false);
  }

  if (!user) return null;

  const isPending = updateMutation.isPending || liveForm.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit User</DialogTitle>
            {viewers.length > 0 && (
              <PresenceAvatars viewers={viewers} currentInstanceId={instanceId} />
            )}
          </div>
          <DialogDescription>
            Update user information and role for {user.email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <LiveField lockedBy={nameField.lockedBy}>
                <Input
                  id="edit-name"
                  type="text"
                  placeholder="John Doe"
                  value={nameField.value ?? user.name ?? ''}
                  onChange={(e) => nameField.onChange(e.target.value)}
                  onFocus={nameField.onFocus}
                  onBlur={nameField.onBlur}
                  required
                  disabled={!nameField.isEditable || isPending}
                />
              </LiveField>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">Role *</Label>
              <LiveField lockedBy={roleField.lockedBy}>
                <Select
                  value={roleField.value ?? user.role ?? 'Viewer'}
                  onValueChange={(value) => roleField.onChange(value)}
                  disabled={!roleField.isEditable || isPending}
                >
                  <SelectTrigger
                    onFocus={roleField.onFocus}
                    onBlur={roleField.onBlur}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Editor">Editor</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                    <SelectItem value="External">External</SelectItem>
                    <SelectItem value="Photographer">Photographer</SelectItem>
                  </SelectContent>
                </Select>
              </LiveField>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
